import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Nav, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../../api';
import { useActiveProject } from '../../../context/ActiveProjectContext';
import {
  useBudgetInvalidator,
  useProjectBaseline,
  useProjectBudgets,
} from '../../../hooks/useBudgetQueries';
import ConfirmationModal from '../../ui/modals/ConfirmationModal';
import notify from '../../ui/feedback/notify';
import { apiFailureDetail } from '../../../utils/apiFailure';
import { getAreaBudgetNavConfig } from './areaBudgetNavConfig';
import {
  canAccessAreaBudgetMenu,
  filterAreaBudgets,
  getAreaBudgetEditPath,
  getAreaBudgetStatusMeta,
  isAreaBudgetRouteActive,
} from './areaBudgetNavUtils';
import { requestAreaBudgetNavigation } from './areaBudgetNavEvents';
import './areaBudgetNavPanel.css';

const FLYOUT_WIDTH = 390;
const FLYOUT_ESTIMATED_HEIGHT = 320;

function useFlyoutPosition(open, anchorRef, panelRef) {
  const [style, setStyle] = useState({ top: 0, left: 0, maxHeight: FLYOUT_ESTIMATED_HEIGHT });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const panelHeight = panel?.offsetHeight || FLYOUT_ESTIMATED_HEIGHT;
    const panelWidth = panel?.offsetWidth || FLYOUT_WIDTH;
    const margin = 6;

    let left = rect.right + margin;
    if (left + panelWidth > window.innerWidth - margin) {
      left = Math.max(margin, rect.left - panelWidth - margin);
    }

    let top = rect.top;
    if (top + panelHeight > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - panelHeight - margin);
    }

    setStyle({
      top,
      left,
      maxHeight: Math.max(160, window.innerHeight - top - margin),
    });
  }, [anchorRef, panelRef]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  return { style, updatePosition };
}

export default function AreaBudgetNavPanel({
  area,
  SubMenuItem,
  hasMenuAccess,
  closeMobileSidebar,
}) {
  const config = getAreaBudgetNavConfig(area);
  const navigate = useNavigate();
  const location = useLocation();
  const { activeProject } = useActiveProject();
  const invalidate = useBudgetInvalidator();
  const projectId = activeProject?.project_id;

  const anchorRef = useRef(null);
  const flyoutRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [budgetToDelete, setBudgetToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { data: approvedBaseline, isLoading: baselineLoading } = useProjectBaseline(
    projectId,
    config?.baselineType,
    { enabled: !!projectId && !!config?.baselineType },
  );

  const { data: allBudgets = [], isLoading: budgetsLoading } = useProjectBudgets(projectId, {
    enabled: !!projectId && !approvedBaseline?.budget_id,
  });

  const areaBudgets = useMemo(
    () => filterAreaBudgets(allBudgets, area),
    [allBudgets, area],
  );
  const approvedBudgetId = approvedBaseline?.budget_id;
  const isBudgetRouteActive = isAreaBudgetRouteActive(area, location.pathname);

  const { style: flyoutStyle, updatePosition } = useFlyoutPosition(open, anchorRef, flyoutRef);

  useEffect(() => {
    if (isBudgetRouteActive) {
      setOpen(false);
    }
  }, [location.pathname, isBudgetRouteActive]);

  useEffect(() => {
    if (open) updatePosition();
  }, [open, budgetsLoading, areaBudgets.length, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (flyoutRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!config || !canAccessAreaBudgetMenu(area, hasMenuAccess)) {
    return null;
  }

  if (baselineLoading) {
    return (
      <Nav.Link className="ps-5 py-1 text-white-50 sidebar-submenu-item">
        Budget
      </Nav.Link>
    );
  }

  if (approvedBudgetId) {
    return (
      <SubMenuItem to={`${config.routePrefix}/${approvedBudgetId}`}>
        Budget
      </SubMenuItem>
    );
  }

  const handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((current) => !current);
  };

  const navigateToAreaBudget = (path, state) => {
    setOpen(false);
    closeMobileSidebar?.();
    const detail = { path, state };
    if (!requestAreaBudgetNavigation(area, detail)) {
      navigate(path, state ? { state } : undefined);
    }
  };

  const handleOpenBudget = (budget) => {
    navigateToAreaBudget(getAreaBudgetEditPath(budget, area));
  };

  const handleNewBudget = (event) => {
    event.preventDefault();
    event.stopPropagation();
    navigateToAreaBudget(config.newPath, { freshBudget: Date.now() });
  };

  const handleDeleteBudget = async () => {
    if (!budgetToDelete || !projectId) return;
    setDeleting(true);
    try {
      await api.delete(`/budgets/${budgetToDelete.budget_id}`);
      await invalidate.projectBudgets(projectId);
      // Cerrar y deseleccionar sólo si de verdad se borró. Antes esto estaba en
      // un `finally`: el borrado fallaba, el `catch` hacía `console.error` y
      // nada más, y el diálogo se cerraba igual. El usuario veía desaparecer la
      // confirmación y se iba convencido de que el presupuesto ya no estaba.
      setBudgetToDelete(null);
    } catch (err) {
      notify.error(apiFailureDetail(err, 'No se pudo borrar el presupuesto'));
    } finally {
      setDeleting(false);
    }
  };

  const isNewBudgetActive = location.pathname === config.newPath
    || (area === 'basecamp' && location.pathname === '/budgets/new');

  const flyoutPanel = open ? createPortal(
    <div
      ref={flyoutRef}
      className="area-budget-nav-flyout"
      style={flyoutStyle}
      role="menu"
      aria-label={`${config.flyoutTitle} menu`}
    >
      <div className="area-budget-nav-flyout-header">{config.flyoutTitle}</div>

      <div className="area-budget-nav-panel">
        {(hasMenuAccess(config.legacyNewMenuRoute) || hasMenuAccess(config.menuRoute)) ? (
          <button
            type="button"
            className={`area-budget-nav-new ${isNewBudgetActive ? 'is-active' : ''}`}
            onClick={handleNewBudget}
          >
            New Budget
          </button>
        ) : null}

        <hr className="area-budget-nav-divider" />

        {budgetsLoading ? (
          <div className="area-budget-nav-loading d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>Loading budgets…</span>
          </div>
        ) : areaBudgets.length === 0 ? (
          <div className="area-budget-nav-empty">No budgets yet.</div>
        ) : (
          <>
            <div className="area-budget-nav-table-head">
              <span>Budget</span>
              <span>Status</span>
              <span className="text-end">Actions</span>
            </div>
            {areaBudgets.map((budget) => {
              const meta = getAreaBudgetStatusMeta(budget);
              return (
                <div key={budget.budget_id} className="area-budget-nav-row">
                  <span className="area-budget-nav-name" title={budget.budget_name}>
                    {budget.budget_name || 'Untitled'}
                  </span>
                  <span className={`area-budget-nav-status ${meta.statusClass}`}>
                    {meta.statusLabel}
                  </span>
                  <span className="area-budget-nav-actions">
                    <button
                      type="button"
                      className="area-budget-nav-action-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenBudget(budget);
                      }}
                    >
                      {meta.actionLabel}
                    </button>
                    {meta.canDelete && (
                      <button
                        type="button"
                        className="area-budget-nav-action-btn danger"
                        onClick={() => setBudgetToDelete(budget)}
                      >
                        Del
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <div ref={anchorRef} className="area-budget-nav-anchor">
        <Nav.Link
          className={`ps-5 py-1 d-flex align-items-center sidebar-submenu-item area-budget-nav-toggle text-white-50 ${open ? 'is-open' : ''} ${isBudgetRouteActive ? 'is-active' : ''}`}
          onClick={handleToggle}
          aria-expanded={open}
          aria-haspopup="menu"
        >
          <span className="flex-grow-1">Budget</span>
          <i className="bi bi-chevron-right sidebar-submenu-chevron" />
        </Nav.Link>
      </div>

      {flyoutPanel}

      <ConfirmationModal
        show={Boolean(budgetToDelete)}
        onHide={() => !deleting && setBudgetToDelete(null)}
        onConfirm={handleDeleteBudget}
        title="Confirm Deletion"
        body={`Are you sure you want to delete the budget "${budgetToDelete?.budget_name}"? This action cannot be undone.`}
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        confirmVariant="danger"
      />
    </>
  );
}
